import {
  Heart,
  Gavel,
  User,
  FileText,
  Plus,
  Building,
  Users,
} from "lucide-react";

// Type definitions for navigation items
export type NavItem = {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  component?: React.ComponentType<{
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }>;
};

export type NavSection = {
  title: string;
  roles: ("all" | "tenant" | "landlord" | "admin")[];
  items: NavItem[];
};

// Consolidated navigation configuration
export const navConfig: NavSection[] = [
  {
    title: "Tenant",
    roles: ["all"],
    items: [
      {
        title: "Favorites",
        description: "Browse your favorite properties",
        href: "/favorites",
        icon: Heart,
      },
      {
        title: "My Bids",
        description: "View and manage your current and past bids",
        href: "/bids",
        icon: Gavel,
      },
    ],
  },
  {
    title: "Landlord",
    roles: ["landlord"],
    items: [
      // {
      //   title: "Business Switcher",
      //   description: "Switch between your businesses",
      //   component: BusinessSelectorModal,
      //   icon: Building,
      // },
      {
        title: "Listings",
        description: "View and manage your property listings",
        href: "/landlord/listings",
        icon: FileText,
      },
      {
        title: "Create listing",
        description: "Create a new property listing",
        // Intentionally no href; UI components will handle generating a new ID and routing
        icon: Plus,
      },
      // {
      //   title: "Comments & Replies",
      //   description: "View and manage comments and replies on your listings",
      //   href: "/landlord/comments",
      //   icon: MessageCircle,
      // },
      // {
      //   title: "Employees",
      //   description: "View and manage your employees",
      //   href: "/landlord/employees",
      //   icon: Users,
      // },
    ],
  },
  {
    title: "Admin",
    roles: ["admin"],
    items: [
      {
        title: "Users",
        description: "Manage users and impersonate for testing and support",
        href: "/admin/users",
        icon: Users,
      },
      {
        title: "Listings",
        description: "Review, approve, and manage all listings",
        href: "/admin/listings",
        icon: FileText,
      },
      {
        title: "Businesses",
        description: "View businesses and impersonate owners",
        href: "/admin/businesses",
        icon: Building,
      },
      {
        title: "Impersonation Logs",
        description: "View system audit logs for impersonation activity",
        href: "/admin/impersonation-logs",
        icon: FileText,
      },
      {
        title: "Who Am I?",
        description: "View your user profile and database records",
        href: "/who-am-i",
        icon: User,
      },
      {
        title: "Resend Email Logs",
        description: "View email sending logs and delivery status",
        href: "/admin/resend-email-logs",
        icon: FileText,
      },
      {
        title: "Resend Webhook Logs",
        description: "View webhook events and delivery confirmations",
        href: "/admin/resend-webhook-logs",
        icon: FileText,
      },
    ],
  },
];

// Helper function to filter sections by user roles
export function getNavSectionsForUser(userRoles: string[]): NavSection[] {
  return navConfig.filter((section) => {
    // Check if user has any of the required roles for this section
    return section.roles.some((role) => {
      if (role === "all") return true;
      return userRoles.includes(role);
    });
  });
}

// Helper function to get all items for a user (flattened)
export function getNavItemsForUser(userRoles: string[]): NavItem[] {
  const allItems: NavItem[] = [];

  navConfig.forEach((section) => {
    // Check if user has any of the required roles for this section
    const hasRequiredRole = section.roles.some((role) => {
      if (role === "all") return true;
      return userRoles.includes(role);
    });

    if (hasRequiredRole) {
      section.items.forEach((item) => {
        allItems.push(item);
      });
    }
  });

  return allItems;
}

// Helper function to get items by type (href vs component)
export function getNavItemsByType(
  items: NavItem[],
  type: "href" | "component",
) {
  return items.filter((item) =>
    type === "href" ? !!item.href : !!item.component,
  );
}
